import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/app';
import { User } from '../../../api/users';
import { userShortName } from '../format';

/** Ограничения DTO бэкенда (ActivatePremiumDto: days 1..3650). */
const MIN_DAYS = 1;
const MAX_DAYS = 3650;
const DEFAULT_DAYS = 30;

interface GrantPremiumDialogProps {
  user: User | null;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  /** Подтверждение запрашивает вызывающий код (useConfirm). */
  onSubmit: (days: number) => Promise<void>;
}

/** Ручная выдача Premium админом: срок в днях → POST /premium/activate. */
export function GrantPremiumDialog(props: GrantPremiumDialogProps) {
  // key сбрасывает поле при открытии для другого пользователя
  return <GrantPremiumDialogInner key={props.user?.id ?? 'closed'} {...props} />;
}

function GrantPremiumDialogInner({ user, isSubmitting, onOpenChange, onSubmit }: GrantPremiumDialogProps) {
  const [days, setDays] = useState(String(DEFAULT_DAYS));
  const parsed = Number(days);
  const valid = Number.isInteger(parsed) && parsed >= MIN_DAYS && parsed <= MAX_DAYS;

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-0 p-0">
        <DialogHeader bordered>
          <DialogTitle>Выдать Premium</DialogTitle>
          <DialogDescription>Пользователь: {user ? userShortName(user) : ''}</DialogDescription>
        </DialogHeader>
        <form
          id="grant-premium-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid && !isSubmitting) void onSubmit(parsed);
          }}
        >
          <DialogBody>
            <FormField
              label="Срок, дней"
              error={!valid ? `Целое число от ${MIN_DAYS} до ${MAX_DAYS}` : undefined}
              hint={valid ? 'Срок считается с текущего момента и заменяет действующий.' : undefined}
            >
              <Input
                type="number"
                inputMode="numeric"
                min={MIN_DAYS}
                max={MAX_DAYS}
                step={1}
                value={days}
                onChange={(e) => setDays(e.target.value)}
                className="tabular-nums"
              />
            </FormField>
          </DialogBody>
        </form>
        <DialogFooter bordered>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button type="submit" form="grant-premium-form" disabled={!valid || isSubmitting}>
            {isSubmitting && <Loader2 className="animate-spin" />}
            Выдать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
